/**
 * Create an iframe inside the placeholder element, if applicable.
 * @param {Element} placeholder
 */
const createIframe = placeholder => {
    if (!placeholder.hasAttribute('data-placeholder-iframe')) {
        return;
    }

    const iframeAttributes = JSON.parse(placeholder.getAttribute('data-placeholder-iframe'));
    const iframe = document.createElement('iframe');
    iframe.setAttribute('data-placeholder-managed-iframe', '');
    Object.entries(iframeAttributes).forEach(([key, value]) => {
        iframe.setAttribute(key, value);
    });
    placeholder.appendChild(iframe);
};

/**
 * Remove the iframe managed by the placeholder, if applicable.
 * @param {Element} placeholder
 */
const removeIframe = placeholder => {
    const iframe = placeholder.querySelector('[data-placeholder-managed-iframe]');
    iframe?.remove();
};

/**
 * Update a placeholder to match the current consent state.
 * @param {Element} placeholder
 */
const updatePlaceholder = placeholder => {
    const category = placeholder.dataset.placeholderCategory;
    const service = placeholder.dataset.placeholderService;

    const preferences = CookieConsent.getUserPreferences();
    const newConsentState =
        preferences.acceptedCategories.includes(category) &&
        preferences.acceptedServices[category].includes(service);

    const existingConsentState = placeholder.hasAttribute('data-placeholder-consent');

    if (existingConsentState === newConsentState) {
        return;
    }

    if (newConsentState) {
        placeholder.setAttribute('data-placeholder-consent', '');
        createIframe(placeholder);
    } else {
        placeholder.removeAttribute('data-placeholder-consent');
        removeIframe(placeholder);
    }

    const placeholderEvent = new CustomEvent('onplaceholderchange', {
        bubbles: true,
        detail: {
            category,
            service,
            consent: newConsentState,
        },
    });

    placeholder.dispatchEvent(placeholderEvent);
};

/**
 * Update all placeholders to reflect consent state changes.
 */
const updatePlaceholders = () => {
    for (const placeholder of document.getElementsByClassName('consent-placeholder')) {
        updatePlaceholder(placeholder);
    }
};

/**
 * Handle button clicks inside consent placeholders.
 * @param {Event} event
 */
const handlePlaceholderButtonClicks = event => {
    if (!event.target?.dataset.placeholderButton) {
        return;
    }

    switch (event.target.dataset.placeholderButton) {
        case 'accept':
            const placeholder = event.target.closest('.consent-placeholder');
            if (!placeholder) return;

            const category = placeholder.dataset.placeholderCategory;
            const service = placeholder.dataset.placeholderService;
            const acceptedServices = CookieConsent.getUserPreferences().acceptedServices[category];
            CookieConsent.acceptService([...new Set([service, ...acceptedServices])], category);
            break;

        case 'preferences':
            CookieConsent.showPreferences();
            break;
    }
};

/**
 * Creates a Mutation Observer to update any placeholders that enter the DOM
 * after the initial DOMContentLoaded event.
 */
const updatePlaceholdersOnDomChanges = () => {
    const observer = new MutationObserver(mutations => {
        mutations.forEach(mutation => {
            mutation.addedNodes.forEach(node => {
                if (node.nodeType !== Node.ELEMENT_NODE) {
                    return;
                }

                if (node.classList.contains('consent-placeholder')) {
                    updatePlaceholder(node);
                }

                if (node.getElementsByClassName('consent-placeholder').length) {
                    updatePlaceholders();
                }
            });
        });
    });

    observer.observe(document.body, { childList: true, subtree: true });
};

window.addEventListener('DOMContentLoaded', updatePlaceholders);
window.addEventListener('DOMContentLoaded', updatePlaceholdersOnDomChanges);
window.addEventListener('cc:onConsent', updatePlaceholders);
window.addEventListener('cc:onChange', updatePlaceholders);
window.addEventListener('click', handlePlaceholderButtonClicks);
